"use client";

import dynamic from "next/dynamic";
import { useEffect } from "react";
import HoistedSharedModules from "@/components/performance/HoistedSharedModules";
import RootLayoutDeferredScripts from "@/components/performance/RootLayoutDeferredScripts";
import { useShouldLoadThirdPartyScripts } from "@/helpers/useShouldLoadThirdPartyScripts";
import { installSafeDataLayer } from "@/helpers/gtag";

/** Non-lead stagger after the shared interaction gate — keeps lead scripts on delay 0. */
const NON_LEAD_STAGGER_MS = {
  webPush: 200,
  countryInfo: 300,
  debugBear: 400,
} as const;

const GTMDelay = dynamic(
  () => import("@/components/organisms/HeaderNew/GTMDelay"),
  { ssr: false },
);
const DebugBearDelay = dynamic(
  () => import("@/components/organisms/HeaderNew/DebugBearDelay"),
  { ssr: false },
);
const WebPushRegister = dynamic(
  () => import("@/components/molecules/FireWebEngageEvents/WebEngageRegister"),
  { ssr: false },
);
const CountryInfo = dynamic(() => import("@/components/CountryInfo"), {
  ssr: false,
});


type Props = {
  webEngageKey?: string;
};

export default function RootLayoutClientHooks({ webEngageKey }: Props) {
  // Isolate GTM/NeoTag tag errors so they never break page JS
  useEffect(() => {
    installSafeDataLayer();
  }, []);

  // Lead-critical: shared 3s delay gate (idle-slot load), no extra stagger.
  // GTM stays gated. VWO is a server Script in the country layout (beforeInteractive).
  // WebEngage loads after hydration via DOM inject (not next/script in this client tree).
  const shouldLoadLeadScripts = useShouldLoadThirdPartyScripts(0);
  // Non-lead: same gate, then short stagger so parse/eval does not pile on one tick.
  const shouldLoadWebPush = useShouldLoadThirdPartyScripts(
    NON_LEAD_STAGGER_MS.webPush,
  );
  const shouldLoadCountryInfo = useShouldLoadThirdPartyScripts(
    NON_LEAD_STAGGER_MS.countryInfo,
  );
  // DebugBear RUM: non-lead monitoring, staggered after the gate.
  const shouldLoadDebugBear = useShouldLoadThirdPartyScripts(
    NON_LEAD_STAGGER_MS.debugBear,
  );

  return (
    <>
      <HoistedSharedModules />
      {/* WebEngage/UTM: afterInteractive — not behind the 3s lead gate */}
      <RootLayoutDeferredScripts webEngageKey={webEngageKey} />
      {shouldLoadWebPush ? <WebPushRegister /> : null}
      {shouldLoadCountryInfo ? <CountryInfo /> : null}
    
      <GTMDelay shouldLoadScripts={shouldLoadLeadScripts} />
      <DebugBearDelay shouldLoadScripts={shouldLoadDebugBear} />
    </>
  );
}
